import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Select, message } from 'antd';
import type { TableTransferDataType } from '../TableTransfer';
import TableTransfer from '../TableTransfer';
import type { User } from '../../Types/interfaces';
import { Roles } from '../../Types/interfaces';
import {
  AssignStudentsToSupervisor,
  GetTeachingStaffOfUniversityDepartment,
  GetUniversityDepartmentsOfDepartmentRepresentative,
  GetUniversityDepartmentsOfSupervisor,
  GetUserById,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import { useHasRole } from '../../hooks/useHasRole';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useInternshipProcessesOfUniversityDepartment } from '../../hooks/useInternshipProcesses';
import { isUserDataFilled } from '../../helper/userHelper';

const SupervisorAssignmentComponent = (): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const hasRole = useHasRole();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [targets, setTargets] = useState<TableTransferDataType[]>([]);
  const { t } = useTranslation();
  const departmentId = Form.useWatch('department', form);
  const selectedSupervisorId = Form.useWatch('supervisor', form);
  const queryClient = useQueryClient();
  const { data: processes = EMPTY_ARRAY, isError: processesError } =
    useInternshipProcessesOfUniversityDepartment(Number(departmentId));

  useEffect(() => {
    if (processesError) {
      messageApi.error(t('supvisAssignComp.fetchError'));
    }
  }, [messageApi, processesError, t]);

  const { data: teachingStaff = EMPTY_ARRAY } = useQuery({
    queryKey: [
      ...QUERY_KEYS.TEACHING_STAFF_OF_UNIVERSITY_DEPARTMENT,
      departmentId,
    ],
    queryFn: async () => {
      return await GetTeachingStaffOfUniversityDepartment(
        departmentId.toString()
      );
    },
    enabled: !!departmentId,
    meta: {
      onError: () => {
        messageApi.error(t('supvisAssignComp.fetchError'));
      },
    },
  });

  const isDepartmentRepresentative = hasRole(Roles.DepartmentRepresentative);

  const { data: departments = EMPTY_ARRAY } = useQuery({
    queryKey: [
      ...QUERY_KEYS.DEPARTMENTS,
      loggedInUser.id,
      isDepartmentRepresentative,
    ],
    queryFn: async () => {
      if (isDepartmentRepresentative) {
        return await GetUniversityDepartmentsOfDepartmentRepresentative(
          loggedInUser.id.toString()
        );
      } else {
        return await GetUniversityDepartmentsOfSupervisor(
          loggedInUser.id.toString()
        );
      }
    },
    enabled: !!loggedInUser.id,
  });

  useEffect(() => {
    if (departments && departments.length === 1) {
      form.setFieldValue('department', departments[0].id);
    }
  }, [departments, form]);

  useEffect(() => {
    if (teachingStaff && teachingStaff.length === 1) {
      form.setFieldValue('supervisor', teachingStaff[0].id);
    }
  }, [form, teachingStaff]);

  const handleSubmit = async (): Promise<void> => {
    try {
      const internshipProcessIds = targets.map((t) => t.process.id);
      const supervisorId = form.getFieldValue('supervisor');
      const departmentId = form.getFieldValue('department');

      if (targets.map((t) => t.tutor.id).includes(supervisorId)) {
        messageApi.open({
          type: 'error',
          content: t('supvisAssignComp.tutorAndSupvisCantBeSame'),
        });
        return;
      }

      const user = await GetUserById(supervisorId);
      if (!isUserDataFilled(user)) {
        messageApi.warning(t('tutorAssignmentComp.userDataMissingWarning'), 7);
        return;
      }

      await AssignStudentsToSupervisor(
        departmentId.toString(),
        supervisorId.toString(),
        internshipProcessIds
      ).then((resp) => {
        if (resp.length !== 0) {
          messageApi.open({
            type: 'warning',
            content: t('supvisAssignComp.assignSomeError'),
          });
        } else {
          messageApi.open({
            type: 'success',
            content: t('supvisAssignComp.assignSuccess'),
          });
        }
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROCESSES_BY_UNIVERSITY_DEPARTMENT,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PENDING_CONVENTIONS,
      });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: t('supvisAssignComp.assignError') + error,
      });
    }
  };

  const getUsername = (user: User): string => {
    if (user.firstName === null) {
      if (user.lastName === null) {
        return user.email;
      }

      return user.lastName.trim() || user.email;
    }

    if (user.lastName === null) {
      return user.firstName.trim() || user.email;
    }

    return `${user.lastName} ${user.firstName}`.trim();
  };

  return (
    <>
      <br />
      {contextHolder}
      <Form form={form} size="large">
        <Row gutter={32}>
          <Col xs={24} lg={12}>
            <Form.Item name="department">
              <Select
                size="large"
                placeholder={t('supvisAssignComp.selDep')}
                options={departments.map((u) => ({
                  label: u.name,
                  value: u.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="supervisor">
              <Select
                size="large"
                placeholder={t('supvisAssignComp.selSupvis')}
                options={teachingStaff.map((u) => ({
                  label: getUsername(u),
                  value: u.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <TableTransfer
            processes={processes}
            setTargets={setTargets}
            selectedSupervisorId={selectedSupervisorId}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            disabled={targets.length === 0}
            onClick={handleSubmit}
            block
          >
            {t('supvisAssignComp.submit')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default SupervisorAssignmentComponent;
